import { useEffect, useState } from 'react'
import { Menu, X } from 'lucide-react'
import { DiscordIcon } from '@/components/BrandIcons'
import LanguageSwitcher from '@/components/LanguageSwitcher'
import { useLanguage } from '@/localization'
import type { CommunityData } from '@/useCommunity'

/**
 * Fixed glassmorphic top navbar — global across the hub (`/` and `/conduct`).
 *
 * - Transparent over the hero; once scrolled past ~24px it frosts into a
 *   parchment glass bar (`bg-parchment/70 backdrop-blur-md` + hairline border).
 * - All links use `/#id` hrefs so they resolve from any route (home scroll or
 *   from the /conduct page). "Conduct" points at the on-page #conduct teaser.
 * - An IntersectionObserver highlights the link for the section in view.
 * - Below `md` the links collapse into a hamburger → translucent glass dropdown.
 *
 * Mirrors the KF1 site Navigation responsive behavior, re-skinned to the hub's
 * parchment + blood-red theme.
 */

/** Section anchors, in the unified hub section order. */
const NAV_LINKS: { id: string; label: string }[] = [
  { id: 'games', label: 'Games' },
  { id: 'community', label: 'Community' },
  { id: 'discord', label: 'Discord' },
  { id: 'conduct', label: 'Conduct' },
  { id: 'team', label: 'Team' },
  { id: 'news', label: 'News' },
  { id: 'contact', label: 'Contact' },
]

export default function Navbar({ community }: { community: CommunityData }) {
  const [scrolled, setScrolled] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [activeId, setActiveId] = useState<string>('')
  const { t } = useLanguage()

  // Frost the bar after the hero, and highlight the section currently in view.
  // Scrollspy by absolute position: the active link is the last section whose
  // top has scrolled just under the fixed navbar. This is recomputed on every
  // scroll, so the highlight never gets stuck — unlike a thin-band
  // IntersectionObserver, which fast scrolls or click-to-scroll can skip.
  useEffect(() => {
    const ids = NAV_LINKS.map((l) => l.id)
    const onScroll = () => {
      setScrolled(window.scrollY > 24)
      // Active = the last section whose top has scrolled into the upper third of
      // the viewport. The generous line (vs. a tight band) means a section that a
      // nav click only parks just under the navbar still registers as active.
      const line = window.innerHeight * 0.3
      let current = ''
      for (const id of ids) {
        const el = document.getElementById(id)
        if (el && el.getBoundingClientRect().top <= line) current = id
      }
      setActiveId(current)
    }
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  const linkBase =
    'relative whitespace-nowrap font-rajdhani text-sm font-semibold uppercase tracking-wider transition-colors duration-200'

  const linkColor = (active: boolean) =>
    active ? 'text-uliunai-red' : 'text-stone-600 hover:text-uliunai-red'

  // Animated red underline that grows on hover and stays lit for the active section.
  const underline = (active: boolean) =>
    `after:absolute after:left-0 after:-bottom-1.5 after:h-0.5 after:bg-uliunai-red after:transition-all after:duration-200 ${
      active ? 'after:w-full' : 'after:w-0 hover:after:w-full'
    }`

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'border-b border-stone-line bg-parchment/70 shadow-card backdrop-blur-md'
          : 'border-b border-transparent bg-transparent'
      }`}
    >
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-6">
        {/* Brand — emblem + wordmark, scrolls to top of home */}
        <a href="/#" className="group flex items-center gap-2.5" aria-label={t('Uliunai home')}>
          <img
            src="/hub-logo.png"
            alt=""
            className="h-9 w-9 object-contain transition-transform duration-200 group-hover:scale-105 sm:h-10 sm:w-10"
          />
          <span className="metal-text font-orbitron text-lg font-black uppercase tracking-[0.1em] sm:text-xl">
            Uliunai
          </span>
        </a>

        {/* Desktop links */}
        <nav className="hidden items-center gap-5 xl:gap-7 lg:flex" aria-label={t('Primary')}>
          {NAV_LINKS.map((l) => {
            const active = activeId === l.id
            return (
              <a
                key={l.id}
                href={`/#${l.id}`}
                onClick={() => setActiveId(l.id)}
                className={`${linkBase} ${linkColor(active)} ${underline(active)}`}
                aria-current={active ? 'true' : undefined}
              >
                {t(l.label)}
              </a>
            )
          })}
        </nav>

        {/* Far-right controls (desktop) — compact CTA (icon + short "Discord")
            so LT/RU don't wrap a long "Join Discord" phrase into the pill. */}
        <div className="hidden shrink-0 items-center gap-3 lg:flex">
          <LanguageSwitcher />
          <a
            href={community.discordInvite}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-uliunai-red px-4 py-2 font-orbitron text-xs font-bold uppercase tracking-wider text-white shadow-card transition-all duration-200 hover:-translate-y-0.5 hover:bg-uliunai-red-dark hover:shadow-glow"
          >
            <DiscordIcon size={16} />
            {t('Discord')}
          </a>
        </div>

        {/* Mobile hamburger */}
        <button
          type="button"
          onClick={() => setMenuOpen((open) => !open)}
          aria-label={menuOpen ? t('Close menu') : t('Open menu')}
          aria-expanded={menuOpen}
          className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-stone-line bg-white/60 text-stone-700 backdrop-blur transition-colors hover:text-uliunai-red lg:hidden"
        >
          {menuOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {/* Mobile dropdown — same glass treatment */}
      {menuOpen && (
        <nav
          aria-label={t('Mobile')}
          className="border-t border-stone-line bg-parchment/80 backdrop-blur-md lg:hidden"
        >
          <div className="mx-auto flex max-w-6xl flex-col px-6 py-4">
            {NAV_LINKS.map((l) => {
              const active = activeId === l.id
              return (
                <a
                  key={l.id}
                  href={`/#${l.id}`}
                  onClick={() => {
                    setMenuOpen(false)
                    setActiveId(l.id)
                  }}
                  className={`py-2.5 font-rajdhani text-sm font-semibold uppercase tracking-wider transition-colors ${linkColor(
                    active,
                  )}`}
                  aria-current={active ? 'true' : undefined}
                >
                  {t(l.label)}
                </a>
              )
            })}
            <LanguageSwitcher compact />
            <a
              href={community.discordInvite}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setMenuOpen(false)}
              className="mt-3 inline-flex items-center justify-center gap-2 rounded-full bg-uliunai-red px-5 py-2.5 font-orbitron text-xs font-bold uppercase tracking-wider text-white shadow-card transition-all duration-200 hover:bg-uliunai-red-dark hover:shadow-glow"
            >
              <DiscordIcon size={16} />
              {t('Join Discord')}
            </a>
          </div>
        </nav>
      )}
    </header>
  )
}
